/**
 * V2 Screen Type Configuration
 *
 * Maps each V2 StepKind to its editable sections for the V2 screen editor.
 * Single source of truth for what sections each V2 screen type can edit.
 *
 * V2 sections are either:
 * - Native V2: work directly with StepNode.content (Sprint 3)
 * - Adapter bridge: convert StepNode↔Screen and delegate to V1 sections (until Sprint 4)
 *
 * Related: v2ScreenTypeConfig is the V2 equivalent of onboardingScreenTypeConfig.ts
 * Sprint: schema-v2-migration, Sprint 3
 */

/**
 * Available editor section types for V2 screens
 *
 * All sections are now native V2 (Sprint 3 + Sprint 4):
 * - headerBar, actionBar, backdrop, copyBlock, basicConfig (Sprint 3)
 * - options, carousel, stepper, textField, roller, media, embed (Sprint 4)
 */
export type V2SectionType =
  | 'headerBar'      // step.content.header — V2 native
  | 'actionBar'      // step.content.actionBar — V2 native
  | 'backdrop'       // step.content.canvas.background — V2 native
  | 'copyBlock'      // step.content.copy + hero — V2 native
  | 'basicConfig'    // step.content.systemPrompt/countdown/offlineFirst — V2 native
  | 'animation'      // step.content.screenAnimation/animated/elementAnimations — V2 native (dedicated motion section, all StepKinds)
  // Sprint 4 native sections:
  | 'options'        // step.content.options — V2 native
  | 'carousel'       // step.content.carousel — V2 native
  | 'stepper'        // step.content.stepper — V2 native
  | 'textField'      // step.content.textField — V2 native
  | 'roller'         // step.content.roller — V2 native
  | 'media'          // step.content.featuredMedia — V2 native
  | 'embed'          // step.content.embed — V2 native
  // Paywall-specific sections (Sprint 4):
  | 'paywallHeader'        // step.content.paywall.closeButton/restoreLabel/headerAppearance
  | 'paywallMedia'         // step.content.featuredMedia + content.paywall.bodyAppearance
  | 'paywallBody'          // step.content.copy + content.options (paywall variant)
  | 'paywallSubscriptions' // step.content.paywall.products
  | 'paywallFooter'        // step.content.paywall.purchaseAction/autoRenewLabel/legalLinks
  // Variable system:
  | 'variableBinding'      // FunnelVariable.boundScreenIds — variable binding, screenBindings, score effects
  // FlexibleLayout:
  | 'flexibleSections'     // step.content.flexibleLayout — sections list + DnD reordering
  | 'stickyContainer'      // step.content.flexibleLayout.stickyContainer — sticky CTA buttons
  // AI mode sections:
  | 'aiMeta'               // AI generation metadata, data mapping, iteration history
  | 'aiAssets'             // AI-generated asset prompts, generation controls
  ;

/** Set of section types that are implemented as native V2 (Sprint 3 + Sprint 4) */
const NATIVE_V2_SECTIONS: ReadonlySet<V2SectionType> = new Set([
  // Sprint 3
  'headerBar',
  'actionBar',
  'backdrop',
  'copyBlock',
  'basicConfig',
  'animation',
  // Sprint 4
  'options',
  'carousel',
  'stepper',
  'textField',
  'roller',
  'media',
  'embed',
  // Sprint 4 — Paywall
  'paywallHeader',
  'paywallMedia',
  'paywallBody',
  'paywallSubscriptions',
  'paywallFooter',
  // Variable system
  'variableBinding',
  // FlexibleLayout
  'stickyContainer',
]);

/**
 * Describes how a section is embedded as a subsection inside another section.
 * Sections listed in `subsections` are NOT rendered as top-level SAccordions;
 * instead, the parent section renders them as plain MUI Accordions inside itself.
 */
export interface SubsectionConfig {
  /** Which section type this embeds inside */
  parent: V2SectionType;
  /** Where inside the parent section: before or after parent's own content */
  position: 'first' | 'last';
  /** Whether the subsection accordion starts expanded */
  defaultExpanded: boolean;
}

/**
 * Configuration for a V2 screen type
 */
export interface V2ScreenTypeConfig {
  /** Display name for UI */
  displayName: string;

  /** Sections available for this screen type in full mode (in render order) */
  sections: V2SectionType[];

  /** Sections available in AI mode (in render order). Falls back to `sections` if not defined. */
  aiSections?: V2SectionType[];

  /** Description of this screen type */
  description?: string;

  /** Valid ItemLayout values for this screen type's options list (if has 'options' section) */
  validItemLayouts?: string[];

  /** Whether items in the options list have per-item action/navigation */
  itemsHaveActions?: boolean;

  /**
   * Whether the Options section offers the one/two-column "Column Layout" selector.
   * false → hidden: the column count is decided by the screen type (lists are one
   * column; FlexibleLayout list sections proxy ListSinglePick, so they hide it too).
   * Stored `content.options.columnLayout` values are left as they are.
   * Omitted → shown whenever itemsHaveActions !== false. Read through
   * shouldShowColumnLayoutSelector().
   */
  showColumnLayoutSelector?: boolean;

  /**
   * If true, this screen type uses its own full-screen dialog editor
   * instead of the standard section panel in OnboardingScreenEditorDialogV2.
   */
  usesCustomEditor?: boolean;

  /**
   * Subsection composition: maps a section type to its parent embedding config.
   * Sections listed here are rendered as plain MUI Accordions inside their parent
   * section, not as standalone SAccordions.
   */
  subsections?: Partial<Record<V2SectionType, SubsectionConfig>>;
}

/**
 * Complete mapping of V2 StepKind values to their editor sections
 *
 * StepKind mapping from V1:
 * - HeroContent ← ScreenImageTitleSubtitles
 * - Carousel ← ScreenSlider
 * - ListMultiPick ← ScreenTableMultipleSelection
 * - ListSinglePick ← ScreenTableSingleSelection
 * - GridMultiPick ← ScreenTwoColumnMultipleSelection
 * - GridSinglePick ← ScreenTwoColumnSingleSelection
 * - ProgressSteps ← ScreenProgressBarTitle
 * - TextInput ← ScreenTitleSubtitleField
 * - WheelPicker ← ScreenTitleSubtitlePicker + ScreenImageTitleSubtitlePicker
 * - HeroList ← ScreenImageTitleSubtitleList
 * - WebEmbed ← CustomScreen
 */
export const V2_SCREEN_TYPE_CONFIGS: Record<string, V2ScreenTypeConfig> = {
  'HeroContent': {
    displayName: 'Hero Content',
    sections: ['headerBar', 'copyBlock', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'aiAssets', 'headerBar', 'actionBar'],
    description: 'Welcome screen with hero image, title, and subtitle',
  },

  'Carousel': {
    displayName: 'Carousel',
    sections: ['headerBar', 'carousel', 'backdrop', 'animation'],
    aiSections: ['aiMeta', 'carousel', 'aiAssets'],
    description: 'Swipeable slides with countdown timer',
  },

  'ListMultiPick': {
    displayName: 'List Multi Pick',
    sections: ['headerBar', 'media', 'copyBlock', 'options', 'variableBinding', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'options', 'aiAssets', 'headerBar', 'actionBar'],
    description: 'Select multiple items from vertical list',
    validItemLayouts: [
      'ImageTitleSubtitle', 'TitleSubtitleImage', 'TitleSubtitle', 'TitleImage', 'ImageTitle', 'Title',
      'TitleSubtitleCheckbox', 'CheckboxTitleSubtitle',
      'ImageTitleSubtitleCheckbox', 'CheckboxTitleSubtitleImage',
      'ImageTitleCheckbox', 'CheckboxTitleImage',
      'TitleCheckbox', 'CheckboxTitle',
    ],
    itemsHaveActions: false,
    subsections: {
      variableBinding: { parent: 'options', position: 'last', defaultExpanded: false },
    },
  },

  'ListSinglePick': {
    displayName: 'List Single Pick',
    sections: ['headerBar', 'media', 'copyBlock', 'options', 'variableBinding', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'options', 'aiAssets', 'headerBar', 'actionBar'],
    description: 'Select one item from vertical list',
    validItemLayouts: [
      'ImageTitleSubtitle', 'TitleSubtitleImage', 'TitleSubtitle', 'TitleImage', 'ImageTitle', 'Title',
    ],
    itemsHaveActions: true,
    // A list is one column by definition — the grid screen types own two columns.
    showColumnLayoutSelector: false,
    subsections: {
      variableBinding: { parent: 'options', position: 'last', defaultExpanded: false },
    },
  },

  'GridMultiPick': {
    displayName: 'Grid Multi Pick',
    sections: ['headerBar', 'media', 'copyBlock', 'options', 'variableBinding', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'options', 'aiAssets', 'headerBar', 'actionBar'],
    description: 'Grid layout with multiple selection',
    validItemLayouts: [
      'Title', 'TitleSubtitle', 'SmallImageTitle', 'MediumImageTitle', 'BigImageTitle', 'FullImage',
    ],
    itemsHaveActions: false,
    subsections: {
      variableBinding: { parent: 'options', position: 'last', defaultExpanded: false },
    },
  },

  'GridSinglePick': {
    displayName: 'Grid Single Pick',
    sections: ['headerBar', 'media', 'copyBlock', 'options', 'variableBinding', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'options', 'aiAssets', 'headerBar', 'actionBar'],
    description: 'Grid layout with single selection',
    validItemLayouts: [
      'Title', 'TitleSubtitle', 'SmallImageTitle', 'MediumImageTitle', 'BigImageTitle', 'FullImage',
    ],
    itemsHaveActions: true,
    subsections: {
      variableBinding: { parent: 'options', position: 'last', defaultExpanded: false },
    },
  },

  'ProgressSteps': {
    displayName: 'Progress Steps',
    sections: ['headerBar', 'stepper', 'animation'],
    aiSections: ['aiMeta', 'stepper', 'aiAssets'],
    description: 'Animated progress bar with segments and countdown',
  },

  'TextInput': {
    displayName: 'Text Input',
    sections: ['headerBar', 'copyBlock', 'textField', 'variableBinding', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'textField', 'headerBar', 'actionBar'],
    description: 'Single text input field',
    subsections: {
      variableBinding: { parent: 'textField', position: 'last', defaultExpanded: false },
    },
  },

  'WheelPicker': {
    displayName: 'Wheel Picker',
    sections: ['headerBar', 'media', 'copyBlock', 'roller', 'variableBinding', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'roller', 'headerBar', 'actionBar'],
    description: 'Dropdown or wheel picker',
    subsections: {
      variableBinding: { parent: 'roller', position: 'last', defaultExpanded: false },
    },
  },

  'HeroList': {
    displayName: 'Hero List',
    sections: ['headerBar', 'copyBlock', 'options', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'copyBlock', 'options', 'aiAssets', 'headerBar', 'actionBar'],
    description: 'Scrollable info list with hero image',
    validItemLayouts: ['ImageTitle', 'TitleImage'],
    itemsHaveActions: false,
  },

  'WebEmbed': {
    displayName: 'Web Embed',
    sections: ['embed', 'headerBar', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'embed', 'headerBar', 'actionBar'],
    description: 'Custom HTML/CSS/JS embedded screen',
  },

  'Paywall': {
    displayName: 'Paywall',
    sections: ['paywallHeader', 'paywallMedia', 'paywallBody', 'paywallSubscriptions', 'paywallFooter', 'backdrop', 'animation', 'basicConfig'],
    aiSections: ['aiMeta', 'paywallBody', 'paywallSubscriptions', 'aiAssets', 'paywallHeader', 'paywallFooter'],
    description: 'Subscription paywall with products, purchase, legal links',
    itemsHaveActions: false,
  },

  /**
   * FlexibleLayout — composable screen built from ordered sections.
   * Edited via standard OnboardingScreenEditorDialogV2 with the
   * 'flexibleSections' right-panel section for drag-and-drop section management.
   */
  'FlexibleLayout': {
    displayName: 'Flexible Layout',
    sections: ['flexibleSections', 'backdrop', 'stickyContainer', 'animation'],
    aiSections: ['aiMeta', 'flexibleSections', 'aiAssets'],
    description: 'Composable screen built from draggable sections (text, media, products, buttons, lists, HTML)',
  },
};

/**
 * Get configuration for a V2 screen type (StepKind)
 *
 * @param stepKind - V2 StepKind value (e.g., 'HeroContent', 'Carousel')
 * @returns Configuration or default if not found
 */
export function getV2ScreenTypeConfig(stepKind: string): V2ScreenTypeConfig {
  return V2_SCREEN_TYPE_CONFIGS[stepKind] ?? {
    displayName: 'Unknown',
    sections: ['headerBar', 'actionBar', 'backdrop', 'animation', 'basicConfig'],
    description: 'Unknown V2 screen type',
  };
}

/**
 * Whether the Options section shows the "Column Layout" selector for this screen type:
 * never on display-only lists (itemsHaveActions: false) and never where the type fixes
 * the column count (showColumnLayoutSelector: false — the list types).
 */
export function shouldShowColumnLayoutSelector(config: V2ScreenTypeConfig): boolean {
  return config.itemsHaveActions !== false && config.showColumnLayoutSelector !== false;
}

/**
 * Check if a section type is implemented as native V2 (Sprint 3)
 * vs adapter bridge (Sprint 4)
 *
 * @param sectionType - Section type to check
 * @returns true if section is native V2
 */
export function isNativeV2Section(sectionType: V2SectionType): boolean {
  return NATIVE_V2_SECTIONS.has(sectionType);
}

/**
 * Allowed V2 StepKind ordering for template panel filtering/sorting.
 * Only screen types listed here appear in the V2 template panel.
 * Numeric values define display order (lower = first).
 */
export const ALLOWED_STEP_KINDS_ORDER: Record<string, number> = {
  HeroContent: 1,
  Carousel: 2,
  ListMultiPick: 3,
  ListSinglePick: 4,
  GridMultiPick: 5,
  GridSinglePick: 6,
  ProgressSteps: 7,
  TextInput: 8,
  WheelPicker: 9,
  HeroList: 10,
  WebEmbed: 11,
  Paywall: 12,
  FlexibleLayout: 13,
};

/**
 * Get all available V2 screen types (StepKind values)
 *
 * @returns Array of all StepKind names
 */
export function getAllV2ScreenTypes(): string[] {
  return Object.keys(V2_SCREEN_TYPE_CONFIGS);
}
